import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  LocalShipping,
  CheckCircle,
  Inventory2,
  Email,
  HourglassEmpty,
  ContentCopy,
  Search as SearchIcon,
  ShoppingBag,
  ReceiptLong,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../../../context/UserContext';
import { apiList, invokeApi, invokeGetApi } from '../../../services/apiServices';
import { InvoiceModal, PURPLE } from '../../common/InvoiceDocument';

const OrderTracking = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUser();

  const [inputOrderId, setInputOrderId] = useState('');
  const [customerOrders, setCustomerOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [invoiceModalOrder, setInvoiceModalOrder] = useState(null);

  // 1. Load customer's past orders for easy dropdown selection
  useEffect(() => {
    if (!user) return;
    const userEmail = (user.email || '').toLowerCase().trim();
    const loaded = [];

    // Local customer orders
    try {
      const localKey = `ar_customer_orders_${userEmail}`;
      const savedLocal = JSON.parse(localStorage.getItem(localKey) || '[]');
      if (Array.isArray(savedLocal)) {
        loaded.push(...savedLocal);
      }
    } catch (e) {}

    // Global dashboard orders that match customer
    try {
      const dashOrders = JSON.parse(localStorage.getItem('ar_dashboard_orders') || '[]');
      if (Array.isArray(dashOrders)) {
        dashOrders.forEach(o => {
          const ordEmail = (o.customer_email || o.email || '').toLowerCase().trim();
          if (ordEmail === userEmail && !loaded.some(item => (item.id || item.order_number) === (o.id || o.order_number))) {
            loaded.push(o);
          }
        });
      }
    } catch (e) {}

    setCustomerOrders(loaded);

    // Check query params (?orderId=...)
    const queryParams = new URLSearchParams(location.search);
    const queryId = queryParams.get('orderId') || queryParams.get('id');

    if (queryId) {
      setInputOrderId(queryId);
      trackSpecificOrder(queryId, loaded);
    } else if (loaded.length > 0) {
      // Default to the most recent order
      const recent = loaded[0];
      const recentId = recent.order_number || recent.id;
      setInputOrderId(recentId);
      trackSpecificOrder(recentId, loaded);
    }
  }, [user, location.search]);

  const trackSpecificOrder = async (orderIdToTrack, preloadedList = customerOrders) => {
    if (!orderIdToTrack?.trim()) {
      setError('Please provide a valid Order ID.');
      return;
    }

    setLoading(true);
    setError('');
    setSelectedOrder(null);

    const cleanId = orderIdToTrack.trim().toLowerCase();

    // 1. Check in preloaded/local list
    let matched = preloadedList.find(o => 
      String(o.order_number || o.id || o.invoice_number || '').toLowerCase() === cleanId
    );

    // 2. If not found locally, try backend /trackOrder API
    if (!matched) {
      try {
        const resTrack = await invokeApi(apiList.trackOrder || '/trackOrder', { order_id: cleanId, orderId: cleanId });
        if (resTrack?.data?.responseCode === "200" && resTrack?.data?.order) {
          matched = resTrack.data.order;
        }
      } catch (e) {}
    }

    if (!matched) {
      try {
        const res = await invokeGetApi(apiList.getOrders || '/getOrders');
        if (res?.data?.orders && Array.isArray(res.data.orders)) {
          matched = res.data.orders.find(o => 
            String(o.order_number || o.id || o.invoice_number || '').toLowerCase() === cleanId
          );
        }
      } catch (e) {}
    }

    // 3. If still not found, check invoices
    if (!matched) {
      try {
        const invRes = await invokeApi(apiList.getInvoices, { search: cleanId });
        const invList = invRes?.data?.data?.invoices || invRes?.data?.invoices || [];
        matched = invList.find(i => 
          String(i.invoice_number || i.order_number || i.id || '').toLowerCase() === cleanId
        );
      } catch (e) {}
    }

    if (matched) {
      const orderDate = matched.date || (matched.created_at ? new Date(matched.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Recent');
      const orderStatus = (matched.order_status || matched.status || 'Processing').toLowerCase();
      const isDelivered = orderStatus.includes('deliver') || orderStatus.includes('complete');

      const rawItems = matched.items || matched.order_items || [];
      const itemsList = Array.isArray(rawItems) && rawItems.length > 0 
        ? rawItems.map(i => `${i.product_name || i.description || i.name || 'Software License'} × ${i.quantity || 1}`)
        : ['Software Genuine License Key × 1'];

      const totalVal = parseFloat(matched.total_amount || matched.totalAmount || matched.total || 0);

      setSelectedOrder({
        id: matched.order_number || matched.invoice_number || matched.id || cleanId.toUpperCase(),
        date: orderDate,
        total: totalVal ? `₹${totalVal.toLocaleString('en-IN')}` : 'Paid',
        rawTotal: totalVal,
        customerName: matched.customer_name || matched.customerName || user?.name || 'Customer',
        email: matched.customer_email || matched.customerEmail || matched.email || user?.email || '',
        phone: matched.customer_phone || matched.customerPhone || matched.phone || user?.phone || '',
        shippingAddress: matched.shipping_address || matched.address || 'Instant Digital Delivery via Registered Email',
        status: isDelivered ? 'Delivered' : 'Processing',
        items: itemsList,
        timeline: [
          {
            title: 'Order Placed & Verified',
            desc: 'Your genuine software license order was received and confirmed.',
            timestamp: orderDate,
            done: true
          },
          {
            title: 'Payment Confirmed',
            desc: `Payment of ${totalVal ? `₹${totalVal.toLocaleString('en-IN')}` : 'Prepaid'} successfully verified.`,
            timestamp: orderDate,
            done: true
          },
          {
            title: 'License Key Allocation',
            desc: 'Digital retail product key generated and bound to your Microsoft account registration.',
            timestamp: isDelivered ? orderDate : 'In progress...',
            done: true
          },
          {
            title: 'Delivered to Registered Email',
            desc: `Genuine digital activation key and setup guide sent to ${matched.customer_email || user?.email || 'your email'}.`,
            timestamp: isDelivered ? orderDate : 'Within 5–10 minutes',
            done: isDelivered
          }
        ],
        rawOrder: matched
      });
    } else {
      // If customer just checked out and order ID is fresh, construct an active tracking entry
      const nowStr = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      setSelectedOrder({
        id: cleanId.toUpperCase(),
        date: nowStr,
        total: 'Prepaid (Processing)',
        rawTotal: 499,
        customerName: user?.name || 'Customer',
        email: user?.email || '',
        phone: user?.phone || '',
        shippingAddress: 'Instant Digital Delivery via Registered Email',
        status: 'Processing',
        items: ['Software Genuine License Key × 1'],
        timeline: [
          {
            title: 'Order Placed & Verified',
            desc: 'Your order was successfully submitted and confirmed.',
            timestamp: nowStr,
            done: true
          },
          {
            title: 'Payment Confirmed',
            desc: 'Prepaid transaction verified via Cashfree Secure Gateway.',
            timestamp: nowStr,
            done: true
          },
          {
            title: 'License Key Allocation',
            desc: 'Allocating authentic lifetime digital key from licensed inventory.',
            timestamp: 'In progress...',
            done: true
          },
          {
            title: 'Delivered to Registered Email',
            desc: `Official product key and instructions will arrive at ${user?.email || 'your email'}.`,
            timestamp: 'Within 5–10 minutes',
            done: false
          }
        ]
      });
    }

    setLoading(false);
  };

  const handleCopy = () => {
    if (selectedOrder?.id) {
      navigator.clipboard.writeText(selectedOrder.id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0E3A7A', display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalShipping sx={{ color: '#0E6ADA' }} /> Track Your Order
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Live status, digital key allocation timeline, and delivery confirmation
          </Typography>
        </Box>
      </Box>

      {/* Order Search & Selection Box */}
      <Paper variant="outlined" sx={{ p: 3, mb: 4, borderRadius: 2.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
        <Grid container spacing={2} alignItems="center">
          {customerOrders.length > 0 && (
            <Grid item xs={12} md={5}>
              <FormControl fullWidth size="small">
                <InputLabel>Select from Your Past Orders</InputLabel>
                <Select
                  value={customerOrders.some(o => (o.order_number || o.id) === inputOrderId) ? inputOrderId : ''}
                  label="Select from Your Past Orders"
                  onChange={(e) => {
                    const id = e.target.value;
                    setInputOrderId(id);
                    trackSpecificOrder(id);
                  }}
                >
                  {customerOrders.map((ord, idx) => {
                    const oid = ord.order_number || ord.id || `ORD-${idx}`;
                    const amt = ord.total_amount || ord.totalAmount || ord.total || 0;
                    return (
                      <MenuItem key={oid} value={oid}>
                        {oid} — ₹{amt} ({ord.order_status || 'Paid'})
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} md={customerOrders.length > 0 ? 5 : 9}>
            <TextField
              fullWidth
              size="small"
              placeholder="Or enter any Order ID (e.g. ORD-1001)..."
              value={inputOrderId}
              onChange={(e) => setInputOrderId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && trackSpecificOrder(inputOrderId)}
            />
          </Grid>

          <Grid item xs={12} md={customerOrders.length > 0 ? 2 : 3}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => trackSpecificOrder(inputOrderId)}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
              disabled={loading}
              sx={{
                bgcolor: '#0E6ADA',
                fontWeight: 700,
                borderRadius: 2,
                py: 1,
                textTransform: 'none',
                '&:hover': { bgcolor: '#0B52A8' }
              }}
            >
              Track
            </Button>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* Tracking Details View */}
      {selectedOrder && (
        <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 3, border: '1.5px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
          {/* Header of Order */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, pb: 2.5, borderBottom: '1px solid #E2E8F0' }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1E293B' }}>
                  Order #{selectedOrder.id}
                </Typography>
                <Button size="small" onClick={handleCopy} sx={{ minWidth: 'auto', p: 0.5, color: copied ? 'success.main' : 'text.secondary' }}>
                  {copied ? <Typography variant="caption" sx={{ fontWeight: 700, color: '#16A34A' }}>Copied!</Typography> : <ContentCopy fontSize="small" />}
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Placed on {selectedOrder.date} · Total: <strong>{selectedOrder.total}</strong>
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip
                label={selectedOrder.status}
                color={selectedOrder.status === 'Delivered' ? 'success' : 'warning'}
                sx={{ fontWeight: 800, px: 1, borderRadius: 1.5 }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<ReceiptLong />}
                onClick={() => setInvoiceModalOrder({
                  invoice_number: selectedOrder.id,
                  customer_name: selectedOrder.customerName,
                  customer_number: selectedOrder.phone,
                  customer_email: selectedOrder.email,
                  address: selectedOrder.shippingAddress,
                  due_date: selectedOrder.date,
                  payment_status: 'Paid',
                  total: selectedOrder.rawTotal || 499,
                  received: selectedOrder.rawTotal || 499,
                  balance: 0,
                  items: selectedOrder.items.map((it, idx) => ({
                    index: idx + 1,
                    description: it,
                    hsn_sac: '997331',
                    quantity: 1,
                    unit: 'Pcs',
                    unit_price: selectedOrder.rawTotal || 499,
                    total_price: selectedOrder.rawTotal || 499
                  }))
                })}
                sx={{
                  borderColor: '#0E6ADA',
                  color: '#0E6ADA',
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: 2,
                  '&:hover': { bgcolor: '#F0F6FF' }
                }}
              >
                Tax Invoice
              </Button>
            </Box>
          </Box>

          {/* Stepper Timeline */}
          <Box sx={{ py: 3.5, px: { xs: 1, sm: 2 } }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1E293B', mb: 3, textTransform: 'uppercase', letterSpacing: 0.8 }}>
              Delivery Progress
            </Typography>

            <Stepper orientation="vertical">
              {selectedOrder.timeline.map((step, idx) => (
                <Step key={step.title} active={step.done} completed={step.done}>
                  <StepLabel
                    StepIconProps={{
                      sx: {
                        color: step.done ? '#16A34A !important' : '#CBD5E1 !important',
                        fontSize: 26
                      }
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: step.done ? '#1E293B' : '#64748B' }}>
                      {step.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#0E6ADA', fontWeight: 600 }}>
                      {step.timestamp}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body2" sx={{ color: '#64748B', pb: 1 }}>
                      {step.desc}
                    </Typography>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Purchased Items & Destination Summary */}
          <Grid container spacing={3} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Items in This Order
              </Typography>
              <Box sx={{ mt: 1 }}>
                {selectedOrder.items.map((it, idx) => (
                  <Typography key={idx} variant="body2" sx={{ fontWeight: 600, color: '#1E293B', mb: 0.5 }}>
                    • {it}
                  </Typography>
                ))}
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Destination & Delivery
              </Typography>
              <Typography variant="body2" sx={{ color: '#475569', mt: 1 }}>
                {selectedOrder.shippingAddress}
              </Typography>
              {selectedOrder.email && (
                <Typography variant="caption" sx={{ color: '#0E6ADA', fontWeight: 600, display: 'block', mt: 0.5 }}>
                  Email Key Delivery: {selectedOrder.email}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Tax Invoice Modal */}
      {invoiceModalOrder && (
        <InvoiceModal
          open={Boolean(invoiceModalOrder)}
          onClose={() => setInvoiceModalOrder(null)}
          data={invoiceModalOrder}
        />
      )}
    </Box>
  );
};

export default OrderTracking;
